<script setup lang="ts">
import { useRouter, useRoute } from 'vue-router'
import { computed } from 'vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import Badge from 'primevue/badge'
import type { Tender } from '@/types/tender'
import HeadingDefault from '@/components/Heading/HeadingDefault.vue'
import { getBadgeStatus } from '@/utils/badgeUtils'
import { useAuthStore } from '@/stores/auth'
import ExcelUpdateBadge from '@/components/badges/ExcelUpdateBadge.vue'

// Use the prop directly without assigning to a variable
const props = defineProps<{
  tender: Tender
}>()

const authStore = useAuthStore()
const userType = authStore.user?.userType
const router = useRouter()
const route = useRoute()

// Get referrer from route query or use navigation history
const backNavigation = computed(() => {
  // Check if referrer is passed in query params
  if (route.query.referrer) {
    const referrer = route.query.referrer as string
    const referrerMap: Record<string, { label: string, path: string }> = {
      // Admin back link
      'dashboard': { label: 'Dashboard', path: '/dashboard' },
      'watchlist-tenders': { label: 'Watchlist Tenders', path: '/watchlist-tenders' },
      'assigned-tenders-admin': { label: 'Assigned Tenders', path: '/assigned-tenders-admin' },
      'in-progress-admin': { label: 'In Progress Tenders', path: '/in-progress-admin' },
      'price-review-admin': { label: 'Price Review', path: '/price-review-admin' },
      'criteria-review-admin': { label: 'Criteria Review', path: '/criteria-review-admin' },
      'ready-to-bid-admin': { label: 'Ready To Bid', path: '/ready-to-bid-admin' },
      'not-fit-admin': { label: 'Not Fit', path: '/not-fit-admin' },
      'not-bidded-admin': { label: 'Not Bidded', path: '/not-bidded-admin' },
      'submitted-tender-admin': { label: 'Submitted Tender', path: '/submitted-tender-admin' },
      'under-query-admin': { label: 'Under Query Tender', path: '/under-query-admin' },
      'rejected-tenders': { label: 'Rejected Tenders', path: '/rejected-tenders' },
      'won-tenders': { label: 'Won Tenders', path: '/won-tenders' },
      'lost-tenders': { label: 'Lost Tenders', path: '/lost-tenders' },
      'engineer-tenders': { label: 'Engineer Tenders', path: '/engineer-tenders' },
      'completed-tenders': { label: 'Completed Tenders', path: '/completed-tenders' },

      // Agent back link
      'assigned-tenders-agent': { label: 'Assigned Tenders', path: '/assigned-tenders-agent' },
      'in-progress-agent': { label: 'In Progress Tenders', path: '/in-progress-agent' },
      'price-review-agent': { label: 'Price Review', path: '/price-review-agent' },
      'criteria-review-agent': { label: 'Criteria Review', path: '/criteria-review-agent' },
      'ready-to-bid-agent': { label: 'Ready To Bid', path: '/ready-to-bid-agent' },
      'not-fit-agent': { label: 'Not Fit', path: '/not-fit-agent' },
      'not-bidded-agent': { label: 'Not Bidded', path: '/not-bidded-agent' },
      'submitted-tender-agent': { label: 'Submitted Tender', path: '/submitted-tender-agent' },
      'under-query-agent': { label: 'Under Query Tender', path: '/under-query-agent' },
      'won-tender-agent': { label: 'Won Tender', path: '/won-tender-agent' },
      'lost-tender-agent': { label: 'Lost Tender', path: '/lost-tender-agent' },
      'completed-tender-agent': { label: 'Completed Tender', path: '/completed-tender-agent' },
    }

    return referrerMap[referrer] || { label: 'Dashboard', path: '/dashboard' }
  }

  // If no referrer in query, try to get from previous route
  try {
    const previousRoute = router.options.history.state.back as string
    if (previousRoute) {
      // Map common routes to labels
      const routeMap: Record<string, string> = {
        '/dashboard': 'Dashboard',
        '/watchlist-tenders': 'Watchlist Tenders',
        '/agent-tenders': 'Agent Tenders',
        '/rejected-tenders': 'Rejected Tenders',
        '/won-tenders': 'Won Tenders',
        '/lost-tenders': 'Lost Tenders',
        '/engineer-tenders': 'Engineer Tenders',
        '/completed-tenders': 'Completed Tenders',
        // '/price-review': 'Price Tenders',
        // '/criteria-review': 'Criteria Tenders',
      }

      const matchingRoute = Object.keys(routeMap).find(routePath =>
        previousRoute.includes(routePath)
      )

      if (matchingRoute) {
        return { label: routeMap[matchingRoute], path: matchingRoute }
      }
    }
  } catch (error) {
    console.log('Could not determine previous route:', error)
  }

  // Default fallback
  return { label: 'Dashboard', path: '/dashboard' }
})

const goBack = () => {
  router.push(backNavigation.value.path)
}

const formatDate = (dateString?: string | Date | null): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid Date'
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return 'Date Error'
  }
}

const getDeadlineClass = (deadline?: string | Date | null): string => {
  if (!deadline) return 'text-gray-600'
  try {
    const now = new Date()
    const deadlineDate = new Date(deadline)
    if (isNaN(deadlineDate.getTime())) return 'text-gray-600'
    const diffTime = deadlineDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    if (diffDays < 0) return 'text-red-600 font-medium'
    if (diffDays <= 7) return 'text-orange-600 font-medium'
    return 'text-green-600 font-medium'
  } catch {
    return 'text-gray-600'
  }
}

const getStatusLabel = (status?: string | null): string => {
  if (!status) return 'Unknown'
  const statusMap: Record<string, string> = {
    new: 'New',
    'watchlist': 'Watch List',
    'reviewed-price': 'Reviewed for Price',
    'reviewed-criteria': 'Reviewed for Criteria',
    'assigned-to-agent': 'Assigned',
    'agent-work': 'Agent Work',
    'price-review': 'Price Review',
    'criteria-review': 'Criteria Review',
    'ready-to-bid': 'Ready to Bid',
    submitted: 'Submitted',
    won: 'Won',
    lost: 'Lost',
    rejected: 'Rejected',
    'assigned-to-engineer': 'Assigned to Engineer',
    'engineer-work': 'Engineer Work',
    completed: 'Completed',
    expired: 'Expired',
    duplicate: 'Duplicate',
    updated: 'Updated',
    existing: 'Existing'
  }
  return statusMap[status] || status
}

// Updated getStatusBadgeClass to use your getBadgeStatus utility
const getStatusBadgeClass = (status?: string | null): string => {
  const badgeStatus = getBadgeStatus(status)

  const classMap: Record<string, string> = {
    success: 'bg-green-100 text-green-800 border border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
    danger: 'bg-red-100 text-red-800 border border-red-200',
    info: 'bg-blue-100 text-blue-800 border border-blue-200',
    neutral: 'bg-gray-100 text-gray-800 border border-gray-200'
  }

  return classMap[badgeStatus] || 'bg-gray-100 text-gray-800 border border-gray-200'
}

// Get sheet type badge class using your utility
const getSheetTypeBadgeClass = (sheetType?: 'Non-GeM' | 'GeM' | null): string => {
  const badgeStatus = getBadgeStatus(sheetType)

  const classMap: Record<string, string> = {
    success: 'bg-green-100 text-green-800 border border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
    danger: 'bg-red-100 text-red-800 border border-red-200',
    info: 'bg-blue-100 text-blue-800 border border-blue-200',
    neutral: 'bg-gray-100 text-gray-800 border border-gray-200'
  }

  return classMap[badgeStatus] || 'bg-gray-100 text-gray-800 border border-gray-200'
}

// Helper to safely access workflowStatus with null check
const getWorkflowStatus = (tender: Tender): string | null | undefined => {
  return tender.workflowStatus
}

// Check if workflow status exists and is not 'new'
const showWorkflowStatusBadge = computed(() => {
  const status = getWorkflowStatus(props.tender)
  return status && status !== 'new'
})

// Format currency for display
const formatCurrency = (amount?: number): string => {
  if (!amount) return '₹0'

  // Format based on amount
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(2)} Cr`
  } else if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(2)} L`
  } else if (amount >= 1000) {
    return `₹${(amount / 1000).toFixed(2)} K`
  }

  return `₹${amount.toLocaleString('en-IN')}`
}
</script>

<template>
  <!-- Back Navigation -->
  <div class="flex items-center gap-2 mb-6">
    <button
      @click="goBack"
      class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M10 19l-7-7m0 0l7-7m-7 7h18"
        />
      </svg>
      Back to {{ backNavigation.label }}
    </button>
    <span class="text-gray-400">/</span>
    <span class="text-sm text-gray-800 dark:text-gray-300">Tender Details</span>
    <span class="text-gray-400">/</span>
    <span class="text-sm font-medium text-blue-600 dark:text-blue-400">{{ props.tender?.t247Id }}</span>
  </div>

  <!-- Tender Header -->
  <CardComponent>
    <template #body>
      <div class="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div class="flex-1">
          <HeadingDefault
            :heading="props.tender?.organization || 'No Description'"
            headingLevel="3"
            :padding="false"
            containerClass="mb-2"
          />

          <div class="flex flex-wrap items-center gap-4 text-sm">
            <div class="flex items-center gap-2">
              <span class="font-medium text-gray-600 dark:text-gray-400">Reference No:</span>
              <span class="font-semibold text-blue-600 dark:text-blue-400">{{ props.tender?.referenceNo || 'N/A' }}</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="font-medium text-gray-600 dark:text-gray-400">T247ID:</span>
              <span class="font-semibold text-blue-600 dark:text-blue-400">
                {{ props.tender?.t247Id || 'N/A' }}
              </span>
            </div>
            <div v-if="userType !== 'engineer'" class="flex items-center gap-2">
              <span class="font-medium text-gray-600 dark:text-gray-400">Deadline:</span>
              <span :class="getDeadlineClass(props.tender?.deadline)" class="font-medium">
                {{ formatDate(props.tender?.deadline) }}
              </span>
            </div>
          </div>

          <!-- Additional Info -->
          <div
            v-if="userType !== 'engineer'"
            class="flex flex-wrap items-center gap-4 text-sm mt-2"
          >
            <div class="flex items-center gap-2">
              <span class="font-medium text-gray-600 dark:text-gray-400">Estimated Cost:</span>
              <span class="font-semibold text-gray-800 dark:text-gray-300">
                {{ formatCurrency(props.tender?.estimatedCost) }}
              </span>
            </div>
            <div class="flex items-center gap-2">
              <span class="font-medium text-gray-600 dark:text-gray-400">MSME Expemtion:</span>
              <span class="font-semibold text-gray-800 dark:text-gray-300">
                {{ props.tender?.msmeExemption }}
              </span>
            </div>
            <div class="flex items-center gap-2">
              <span class="font-medium text-gray-600 dark:text-gray-400">EMD Value:</span>
              <span class="font-semibold text-gray-800 dark:text-gray-300">
                {{ props.tender?.emd }}
              </span>
            </div>
            <div class="flex items-center gap-2">
              <span class="font-medium text-gray-600 dark:text-gray-400">Document Fees:</span>
              <span class="font-semibold text-gray-800 dark:text-gray-300">
                {{ props.tender?.documentFees }}
              </span>
            </div>
          </div>

          <!-- Changes from the last Excel re-upload (shown for 7 days) -->
          <ExcelUpdateBadge :tender="props.tender" variant="banner" />
        </div>
        <div class="flex flex-wrap gap-2">
          <!-- Sheet Type Badge -->
          <Badge
            :value="props.tender?.sheetType || 'Unknown'"
            :class="getSheetTypeBadgeClass(props.tender?.sheetType)"
          />

          <!-- Status Badge -->
          <!-- <Badge
            :value="getStatusLabel(props.tender?.status)"
            :class="getStatusBadgeClass(props.tender?.status)"
          /> -->

          <!-- Workflow Status Badge if available -->
          <Badge
            v-if="showWorkflowStatusBadge"
            :value="getStatusLabel(getWorkflowStatus(props.tender))"
            :class="getStatusBadgeClass(getWorkflowStatus(props.tender))"
          />
        </div>
      </div>
    </template>
  </CardComponent>
</template>
